import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { 
  Plus, Play, Pause, Trash2, Edit, MoreVertical, Target, DollarSign,
  Copy, CheckSquare, Square, ArrowUpDown, ArrowUp, ArrowDown, 
  ChevronDown, Filter, X, Percent, CheckCircle, XCircle, Clock, Zap, Settings2
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  getCampaigns, deleteCampaign, activateCampaign, pauseCampaign,
  duplicateCampaign, bulkActivateCampaigns, bulkPauseCampaigns, bulkDeleteCampaigns,
  updateCampaign, approveCampaign, rejectCampaign, setCampaignRunMode
} from "../lib/api";

// Approval Status Badge Component
function ApprovalBadge({ status }) {
  const config = {
    approved: { icon: CheckCircle, class: "bg-green-100 text-green-700 border-green-200", label: "Approved" },
    pending: { icon: Clock, class: "bg-amber-100 text-amber-700 border-amber-200", label: "Pending" },
    rejected: { icon: XCircle, class: "bg-red-100 text-red-700 border-red-200", label: "Rejected" }
  };
  
  const { icon: Icon, class: className, label } = config[status] || config.pending;
  
  return (
    <Badge variant="outline" className={`${className} font-medium text-[10px] tracking-wider px-2 py-0.5 flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
}

// Run Mode Badge Component
function RunModeBadge({ mode }) {
  const config = {
    automatic: { icon: Zap, class: "bg-blue-100 text-blue-700 border-blue-200", label: "Auto" },
    manual: { icon: Settings2, class: "bg-slate-100 text-slate-700 border-slate-200", label: "Manual" }
  };
  
  const { icon: Icon, class: className, label } = config[mode] || config.automatic;
  
  return (
    <Badge variant="outline" className={`${className} font-medium text-[10px] tracking-wider px-2 py-0.5 flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const variants = {
    active: "badge-success",
    paused: "badge-warning", 
    draft: "badge-neutral",
    completed: "badge-info",
    error: "badge-error"
  };
  
  return (
    <Badge 
      variant="outline" 
      className={`${variants[status] || variants.draft} font-medium uppercase text-[10px] tracking-wider px-2 py-0.5`}
    >
      <span className={`status-dot status-${status} mr-1.5`}></span>
      {status}
    </Badge>
  );
}

// Sortable Column Header Component
function SortableHeader({ label, sortKey, currentSort, onSort }) {
  const isActive = currentSort.key === sortKey;
  const isAsc = currentSort.direction === "asc";
  
  return (
    <button
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 transition-colors group"
    >
      {label}
      <span className="opacity-50 group-hover:opacity-100">
        {isActive ? (
          isAsc ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowUpDown className="w-3 h-3" />
        )}
      </span>
    </button>
  );
}

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [sort, setSort] = useState({ key: "created_at", direction: "desc" });
  const [statusFilter, setStatusFilter] = useState("all");
  const [userRole, setUserRole] = useState(null); // Track user role for conditional rendering
  
  // Markup modal state
  const [markupModalOpen, setMarkupModalOpen] = useState(false);
  const [markupCampaign, setMarkupCampaign] = useState(null);
  const [markupValue, setMarkupValue] = useState(0);
  const [savingMarkup, setSavingMarkup] = useState(false);
  
  // Approval modal state (Super Admin only)
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalCampaign, setApprovalCampaign] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null); // 'approve' or 'reject'
  const [approvalNotes, setApprovalNotes] = useState("");
  const [savingApproval, setSavingApproval] = useState(false);
  
  // Run mode modal state (Super Admin only)
  const [runModeModalOpen, setRunModeModalOpen] = useState(false);
  const [runModeCampaign, setRunModeCampaign] = useState(null);
  const [selectedRunMode, setSelectedRunMode] = useState("automatic");
  const [savingRunMode, setSavingRunMode] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [pagination, setPagination] = useState({ total_count: 0, total_pages: 1 });

  const fetchCampaigns = useCallback(async (status = "all", page = 1) => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (status !== "all") params.status = status;
      const response = await getCampaigns(params);
      
      // Handle new paginated response format
      if (response.data.campaigns) {
        setCampaigns(response.data.campaigns);
        setPagination(response.data.pagination);
        // Track user role for conditional rendering
        if (response.data.user_role) {
          setUserRole(response.data.user_role);
        }
      } else {
        // Fallback for old format
        setCampaigns(response.data);
      }
    } catch (error) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    fetchCampaigns(statusFilter, currentPage);
  }, [fetchCampaigns, statusFilter, currentPage]);

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to first page
    setSelectedIds(new Set()); // Clear selection on filter change
  };
  
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedIds(new Set()); // Clear selection on page change
  };

  // Sorted campaigns
  const sortedCampaigns = useMemo(() => {
    const sorted = [...campaigns].sort((a, b) => {
      let aVal, bVal;
      
      switch (sort.key) {
        case "name":
          aVal = a.name?.toLowerCase() || "";
          bVal = b.name?.toLowerCase() || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "bid_price":
          aVal = a.bid_price || 0;
          bVal = b.bid_price || 0;
          break;
        case "ecpm":
          aVal = a.ecpm || 0;
          bVal = b.ecpm || 0;
          break;
        case "today_impressions":
          aVal = a.today_impressions || 0;
          bVal = b.today_impressions || 0;
          break;
        case "total_impressions":
          aVal = a.total_impressions || 0;
          bVal = b.total_impressions || 0;
          break;
        case "today_spend":
          aVal = a.today_spend || 0;
          bVal = b.today_spend || 0;
          break;
        case "total_spend":
          aVal = a.total_spend || 0;
          bVal = b.total_spend || 0;
          break;
        case "created_at":
        default:
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
      }
      
      if (typeof aVal === "string") {
        return sort.direction === "asc" 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      return sort.direction === "asc" ? aVal - bVal : bVal - aVal;
    });
    
    return sorted;
  }, [campaigns, sort]);

  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === campaigns.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(campaigns.map(c => c.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Single campaign actions
  const handleActivate = async (id) => {
    try {
      await activateCampaign(id);
      toast.success("Campaign activated");
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to activate campaign");
    }
  };

  const handlePause = async (id) => {
    try {
      await pauseCampaign(id);
      toast.success("Campaign paused");
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to pause campaign");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteCampaign(deleteId);
      toast.success("Campaign deleted");
      setDeleteId(null);
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await duplicateCampaign(id);
      toast.success(`Campaign duplicated: ${response.data.name}`);
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to duplicate campaign");
    }
  };

  // Bulk actions
  const handleBulkActivate = async () => {
    try {
      const ids = Array.from(selectedIds);
      await bulkActivateCampaigns(ids);
      toast.success(`${ids.length} campaigns activated`);
      clearSelection();
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to activate campaigns");
    }
  };

  const handleBulkPause = async () => {
    try {
      const ids = Array.from(selectedIds);
      await bulkPauseCampaigns(ids);
      toast.success(`${ids.length} campaigns paused`);
      clearSelection();
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to pause campaigns");
    }
  };

  const handleBulkDelete = async () => {
    try {
      const ids = Array.from(selectedIds);
      await bulkDeleteCampaigns(ids);
      toast.success(`${ids.length} campaigns deleted`);
      clearSelection();
      setShowBulkDelete(false);
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to delete campaigns");
    }
  };

  // Markup handlers
  const openMarkupModal = (campaign) => {
    setMarkupCampaign(campaign);
    // Set the appropriate markup value based on user role
    if (userRole === 'super_admin') {
      setMarkupValue(campaign.markup?.super_admin_markup_percent || 0);
    } else if (userRole === 'admin') {
      setMarkupValue(campaign.markup?.admin_markup_percent || 0);
    }
    setMarkupModalOpen(true);
  };

  const handleSaveMarkup = async () => {
    if (!markupCampaign) return;
    
    setSavingMarkup(true);
    try {
      // Prepare markup payload based on role
      const markupPayload = {};
      if (userRole === 'super_admin') {
        markupPayload.super_admin_markup_percent = parseFloat(markupValue) || 0;
        // Preserve admin markup if it exists
        if (markupCampaign.markup?.admin_markup_percent) {
          markupPayload.admin_markup_percent = markupCampaign.markup.admin_markup_percent;
        }
      } else if (userRole === 'admin') {
        markupPayload.admin_markup_percent = parseFloat(markupValue) || 0;
      }
      
      await updateCampaign(markupCampaign.id, { markup: markupPayload });
      toast.success("Markup updated successfully");
      setMarkupModalOpen(false);
      setMarkupCampaign(null);
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to update markup");
    } finally {
      setSavingMarkup(false);
    }
  };

  // Approval handlers (Super Admin only)
  const openApprovalModal = (campaign, action) => {
    setApprovalCampaign(campaign);
    setApprovalAction(action);
    setApprovalNotes("");
    setApprovalModalOpen(true);
  };

  const handleApproval = async () => {
    if (!approvalCampaign || !approvalAction) return;
    
    setSavingApproval(true);
    try {
      if (approvalAction === 'approve') {
        await approveCampaign(approvalCampaign.id, approvalNotes || null);
        toast.success("Campaign approved successfully");
      } else {
        await rejectCampaign(approvalCampaign.id, approvalNotes || null);
        toast.success("Campaign rejected");
      }
      setApprovalModalOpen(false);
      setApprovalCampaign(null);
      setApprovalAction(null);
      setApprovalNotes("");
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error(`Failed to ${approvalAction} campaign`);
    } finally {
      setSavingApproval(false);
    }
  };

  // Run mode handlers (Super Admin only)
  const openRunModeModal = (campaign) => {
    setRunModeCampaign(campaign);
    setSelectedRunMode(campaign.run_mode || "automatic");
    setRunModeModalOpen(true);
  };

  const handleRunModeChange = async () => {
    if (!runModeCampaign) return;
    
    setSavingRunMode(true);
    try {
      await setCampaignRunMode(runModeCampaign.id, selectedRunMode);
      toast.success(`Run mode set to ${selectedRunMode}`);
      setRunModeModalOpen(false);
      setRunModeCampaign(null);
      fetchCampaigns(statusFilter, currentPage);
    } catch (error) {
      toast.error("Failed to update run mode");
    } finally {
      setSavingRunMode(false);
    }
  };

  const formatCurrency = (num) => `$${num?.toFixed(2) || '0.00'}`;
  const formatNumber = (num) => num?.toLocaleString() || '0';

  const isAllSelected = campaigns.length > 0 && selectedIds.size === campaigns.length;
  const isSomeSelected = selectedIds.size > 0;

  return (
    <div className="p-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Campaigns</h1>
          <p className="text-sm text-slate-600 mt-1">Manage your advertising campaigns</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Status Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="status-filter-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                {statusFilter === "all" ? "All Status" : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem 
                onClick={() => handleStatusFilterChange("all")}
                className={statusFilter === "all" ? "bg-slate-100" : ""}
              >
                All Status
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleStatusFilterChange("active")}
                className={statusFilter === "active" ? "bg-slate-100" : ""}
              >
                <span className="status-dot status-active mr-2"></span>
                Active
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleStatusFilterChange("paused")}
                className={statusFilter === "paused" ? "bg-slate-100" : ""}
              >
                <span className="status-dot status-paused mr-2"></span>
                Paused
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleStatusFilterChange("draft")}
                className={statusFilter === "draft" ? "bg-slate-100" : ""}
              >
                <span className="status-dot status-draft mr-2"></span>
                Draft
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleStatusFilterChange("completed")}
                className={statusFilter === "completed" ? "bg-slate-100" : ""}
              >
                <span className="status-dot status-completed mr-2"></span>
                Completed
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Only show Create Campaign button for Advertisers */}
          {userRole === 'advertiser' && (
            <Link to="/campaigns/new">
              <Button 
                className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white btn-press shadow-[0_0_10px_rgba(59,130,246,0.3)]"
                data-testid="create-campaign-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </Link>
          )}
          
          {/* Show info badge for non-advertisers */}
          {(userRole === 'super_admin' || userRole === 'admin') && (
            <div className="text-xs text-slate-500 bg-slate-100 px-3 py-1.5 rounded-md">
              View & Edit Only
            </div>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {isSomeSelected && (
        <div className="mb-4 p-3 rounded-lg bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5 text-[#3B82F6]" />
            <span className="text-sm text-slate-900">
              {selectedIds.size} campaign{selectedIds.size > 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkActivate}
              className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10"
            >
              <Play className="w-3 h-3 mr-1" />
              Activate
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkPause}
              className="border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"
            >
              <Pause className="w-3 h-3 mr-1" />
              Pause
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowBulkDelete(true)}
              className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              className="text-slate-500 hover:text-slate-900"
            >
              <X className="w-3 h-3 mr-1" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Campaigns List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Loading campaigns...</div>
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="surface-primary border-panel">
          <CardContent className="empty-state py-16">
            <Target className="empty-state-icon" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No campaigns yet</h3>
            <p className="text-sm text-slate-600 mb-4">
              {userRole === 'advertiser' 
                ? "Create your first campaign to start bidding" 
                : "No campaigns available to view"}
            </p>
            {userRole === 'advertiser' && (
              <Link to="/campaigns/new">
                <Button className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Campaign
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="campaigns-list">
          {/* Table Header */}
          <div className="px-4 py-2 flex items-center gap-4 text-xs text-slate-500">
            <div className="w-8">
              <Checkbox
                checked={isAllSelected}
                onCheckedChange={toggleSelectAll}
                className="border-slate-200 data-[state=checked]:bg-[#3B82F6] data-[state=checked]:border-[#3B82F6]"
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <SortableHeader label="Name" sortKey="name" currentSort={sort} onSort={handleSort} />
            </div>
            {/* Show Admin column only for Super Admin */}
            {userRole === 'super_admin' && (
              <div className="w-28">
                <span className="text-xs text-slate-500">Admin</span>
              </div>
            )}
            {/* Show Advertiser column for Super Admin and Admin */}
            {(userRole === 'super_admin' || userRole === 'admin') && (
              <div className="w-28">
                <span className="text-xs text-slate-500">Advertiser</span>
              </div>
            )}
            <div className="w-20">
              <SortableHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} />
            </div>
            {/* Approval Status column - Show to ALL roles */}
            <div className="w-24">
              <span className="text-xs text-slate-500">Approval</span>
            </div>
            {/* Run Mode column - Only for Super Admin */}
            {userRole === 'super_admin' && (
              <div className="w-20">
                <span className="text-xs text-slate-500">Mode</span>
              </div>
            )}
            <div className="w-20 text-right">
              <SortableHeader label="Bid CPM" sortKey="bid_price" currentSort={sort} onSort={handleSort} />
            </div>
            <div className="w-20 text-right">
              <SortableHeader label="eCPM" sortKey="ecpm" currentSort={sort} onSort={handleSort} />
            </div>
            <div className="w-20 text-right">
              <SortableHeader label="Today Imps" sortKey="today_impressions" currentSort={sort} onSort={handleSort} />
            </div>
            <div className="w-20 text-right">
              <SortableHeader label="Total Imps" sortKey="total_impressions" currentSort={sort} onSort={handleSort} />
            </div>
            <div className="w-24 text-right">
              <SortableHeader label="Today Spend" sortKey="today_spend" currentSort={sort} onSort={handleSort} />
            </div>
            <div className="w-24 text-right">
              <SortableHeader label="Total Spend" sortKey="total_spend" currentSort={sort} onSort={handleSort} />
            </div>
            {/* Markup column - Only for Super Admin and Admin */}
            {(userRole === 'super_admin' || userRole === 'admin') && (
              <div className="w-20 text-right">
                <span className="text-xs text-slate-500">Markup</span>
              </div>
            )}
            <div className="w-28 text-right">Actions</div>
          </div>

          {/* Campaign Rows */}
          {sortedCampaigns.map((campaign) => (
            <Card 
              key={campaign.id} 
              className={`surface-primary border-panel card-hover transition-all ${
                selectedIds.has(campaign.id) ? 'ring-1 ring-[#3B82F6] bg-[#3B82F6]/5' : ''
              }`}
              data-testid={`campaign-${campaign.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Checkbox */}
                  <div className="w-8">
                    <Checkbox
                      checked={selectedIds.has(campaign.id)}
                      onCheckedChange={() => toggleSelect(campaign.id)}
                      className="border-slate-200 data-[state=checked]:bg-[#3B82F6] data-[state=checked]:border-[#3B82F6]"
                    />
                  </div>
                  
                  {/* Name & ID */}
                  <div className="flex-1 min-w-[180px]">
                    <h3 className="text-sm font-semibold text-slate-900">{campaign.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5 font-mono truncate">ID: {campaign.id}</p>
                  </div>
                  
                  {/* Admin Name - Only for Super Admin */}
                  {userRole === 'super_admin' && (
                    <div className="w-28">
                      <p className="text-xs text-slate-600 truncate" title={campaign.admin_name || '-'}>
                        {campaign.admin_name || '-'}
                      </p>
                    </div>
                  )}
                  
                  {/* Advertiser Name - For Super Admin and Admin */}
                  {(userRole === 'super_admin' || userRole === 'admin') && (
                    <div className="w-28">
                      <p className="text-xs text-slate-600 truncate" title={campaign.advertiser_name || '-'}>
                        {campaign.advertiser_name || '-'}
                      </p>
                    </div>
                  )}
                  
                  {/* Status */}
                  <div className="w-20">
                    <StatusBadge status={campaign.status} />
                  </div>
                  
                  {/* Approval Status - Show to ALL roles */}
                  <div className="w-24">
                    <div className="flex flex-col gap-1">
                      <ApprovalBadge status={campaign.approval_status || 'pending'} />
                      {/* Show rejection reason for rejected campaigns */}
                      {campaign.approval_status === 'rejected' && campaign.approval_notes && (
                        <p className="text-[10px] text-red-600 truncate" title={campaign.approval_notes}>
                          {campaign.approval_notes}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Run Mode - Only for Super Admin */}
                  {userRole === 'super_admin' && (
                    <div className="w-20">
                      <RunModeBadge mode={campaign.run_mode || 'automatic'} />
                    </div>
                  )}
                  
                  {/* Bid CPM */}
                  <div className="w-20 text-right">
                    <p className="text-sm font-mono text-[#3B82F6]">{formatCurrency(campaign.bid_price)}</p>
                  </div>
                  
                  {/* eCPM */}
                  <div className="w-20 text-right">
                    <p className="text-sm font-mono text-[#F59E0B]">{formatCurrency(campaign.ecpm || 0)}</p>
                  </div>
                  
                  {/* Today Impressions */}
                  <div className="w-20 text-right">
                    <p className="text-sm font-mono text-[#8B5CF6]">{formatNumber(campaign.today_impressions || 0)}</p>
                  </div>
                  
                  {/* Total Impressions */}
                  <div className="w-20 text-right">
                    <p className="text-sm font-mono text-[#8B5CF6]">{formatNumber(campaign.total_impressions || 0)}</p>
                  </div>
                  
                  {/* Today Spend */}
                  <div className="w-24 text-right">
                    <p className="text-sm font-mono text-[#10B981]">{formatCurrency(campaign.today_spend || 0)}</p>
                  </div>
                  
                  {/* Total Spend */}
                  <div className="w-24 text-right">
                    <p className="text-sm font-mono text-[#10B981]">{formatCurrency(campaign.total_spend || 0)}</p>
                  </div>
                  
                  {/* Markup Column - Only for Super Admin and Admin */}
                  {(userRole === 'super_admin' || userRole === 'admin') && (
                    <div className="w-20 text-right">
                      {userRole === 'super_admin' ? (
                        <p className="text-sm font-mono text-amber-600">
                          {campaign.markup?.super_admin_markup_percent > 0 
                            ? `${campaign.markup.super_admin_markup_percent}%` 
                            : '-'}
                        </p>
                      ) : (
                        <p className="text-sm font-mono text-amber-600">
                          {campaign.markup?.admin_markup_percent > 0 
                            ? `${campaign.markup.admin_markup_percent}%` 
                            : '-'}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="w-28 flex items-center justify-end gap-1">
                    {campaign.status === "active" ? (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handlePause(campaign.id)}
                        className="h-8 w-8 p-0 text-[#F59E0B] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10"
                        title="Pause"
                      >
                        <Pause className="w-4 h-4" />
                      </Button>
                    ) : campaign.status !== "completed" && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleActivate(campaign.id)}
                        className="h-8 w-8 p-0 text-[#10B981] hover:text-[#10B981] hover:bg-[#10B981]/10"
                        title="Activate"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    )}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent 
                        align="end"
                        className="surface-primary border-panel w-40"
                      >
                        <DropdownMenuItem asChild>
                          <Link 
                            to={`/campaigns/${campaign.id}/edit`}
                            className="flex items-center cursor-pointer"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDuplicate(campaign.id)}
                          className="cursor-pointer"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        {/* Markup option - Only for Super Admin and Admin */}
                        {(userRole === 'super_admin' || userRole === 'admin') && (
                          <>
                            <DropdownMenuSeparator className="bg-slate-200" />
                            <DropdownMenuItem 
                              onClick={() => openMarkupModal(campaign)}
                              className="cursor-pointer text-amber-600 focus:text-amber-700"
                            >
                              <Percent className="w-4 h-4 mr-2" />
                              Markup
                            </DropdownMenuItem>
                          </>
                        )}
                        {/* Approval options - Only for Super Admin */}
                        {userRole === 'super_admin' && (
                          <>
                            <DropdownMenuSeparator className="bg-slate-200" />
                            {campaign.approval_status !== 'approved' && (
                              <DropdownMenuItem 
                                onClick={() => openApprovalModal(campaign, 'approve')}
                                className="cursor-pointer text-green-600 focus:text-green-700"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve
                              </DropdownMenuItem>
                            )}
                            {campaign.approval_status !== 'rejected' && (
                              <DropdownMenuItem 
                                onClick={() => openApprovalModal(campaign, 'reject')}
                                className="cursor-pointer text-red-600 focus:text-red-700"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator className="bg-slate-200" />
                            <DropdownMenuItem 
                              onClick={() => openRunModeModal(campaign)}
                              className="cursor-pointer text-blue-600 focus:text-blue-700"
                            >
                              <Settings2 className="w-4 h-4 mr-2" />
                              Run Mode
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator className="bg-slate-200" />
                        <DropdownMenuItem 
                          onClick={() => setDeleteId(campaign.id)}
                          className="text-[#EF4444] focus:text-[#EF4444] cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog (Single) */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="surface-primary border-panel">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Delete Campaign</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Are you sure you want to delete this campaign? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-[#EF4444] hover:bg-[#EF4444]/90 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDelete} onOpenChange={setShowBulkDelete}>
        <AlertDialogContent className="surface-primary border-panel">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Delete {selectedIds.size} Campaigns</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Are you sure you want to delete {selectedIds.size} campaign{selectedIds.size > 1 ? 's' : ''}? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete}
              className="bg-[#EF4444] hover:bg-[#EF4444]/90 text-white"
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between mt-6 px-2">
          <div className="text-sm text-slate-600">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total_count)} of {pagination.total_count} campaigns
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="border-slate-200"
            >
              Previous
            </Button>
            
            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum;
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className={currentPage === pageNum ? "bg-[#3B82F6]" : "border-slate-200"}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
              className="border-slate-200"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Markup Modal */}
      <Dialog open={markupModalOpen} onOpenChange={setMarkupModalOpen}>
        <DialogContent className="surface-primary border-panel sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Percent className="w-5 h-5 text-amber-600" />
              {userRole === 'super_admin' ? 'Super Admin Markup' : 'Markup'}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {markupCampaign && (
                <>
                  Set your markup percentage for <strong>{markupCampaign.name}</strong>
                  <br />
                  <span className="text-xs text-slate-500">
                    Bid: ${markupCampaign.bid_price?.toFixed(2) || '0.00'} CPM
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Show other markup info (read-only) */}
            {userRole === 'super_admin' && markupCampaign?.markup?.admin_markup_percent > 0 && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600">Admin Markup:</span>
                  <span className="font-mono font-medium text-slate-800">
                    {markupCampaign.markup.admin_markup_percent}%
                  </span>
                </div>
              </div>
            )}
            
            {/* Editable markup field */}
            <div className="space-y-2">
              <Label className="text-slate-700">
                {userRole === 'super_admin' ? 'Your Markup (%)' : 'Markup (%)'}
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={markupValue}
                  onChange={(e) => setMarkupValue(e.target.value)}
                  className="border-slate-200 pr-8"
                  data-testid="markup-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
              </div>
              <p className="text-xs text-slate-500">
                This percentage will be deducted from the bid before sending to SSP
              </p>
            </div>
            
            {/* Preview calculation */}
            {markupCampaign && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-2">Bid Breakdown</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Advertiser Bid:</span>
                    <span className="font-mono">${markupCampaign.bid_price?.toFixed(2) || '0.00'}</span>
                  </div>
                  {userRole === 'super_admin' && markupCampaign.markup?.admin_markup_percent > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>- Admin ({markupCampaign.markup.admin_markup_percent}%):</span>
                      <span className="font-mono">
                        -${((markupCampaign.bid_price || 0) * (markupCampaign.markup.admin_markup_percent / 100)).toFixed(4)}
                      </span>
                    </div>
                  )}
                  {parseFloat(markupValue) > 0 && (
                    <div className="flex justify-between text-amber-700">
                      <span>- {userRole === 'super_admin' ? 'Your Markup' : 'Markup'} ({markupValue}%):</span>
                      <span className="font-mono">
                        -${(
                          ((markupCampaign.bid_price || 0) * 
                          (1 - (markupCampaign.markup?.admin_markup_percent || 0) / 100)) * 
                          (parseFloat(markupValue) / 100)
                        ).toFixed(4)}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-amber-200 mt-2 pt-2 flex justify-between font-medium">
                    <span className="text-slate-700">SSP Receives:</span>
                    <span className="font-mono text-green-600">
                      ${(
                        (markupCampaign.bid_price || 0) * 
                        (1 - (markupCampaign.markup?.admin_markup_percent || 0) / 100) *
                        (1 - (parseFloat(markupValue) || 0) / 100)
                      ).toFixed(4)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setMarkupModalOpen(false)}
              className="border-slate-200"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveMarkup}
              disabled={savingMarkup}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="save-markup-btn"
            >
              {savingMarkup ? "Saving..." : "Save Markup"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Modal (Super Admin only) */}
      <Dialog open={approvalModalOpen} onOpenChange={setApprovalModalOpen}>
        <DialogContent className="surface-primary border-panel sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              {approvalAction === 'approve' ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Approve Campaign
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  Reject Campaign
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {approvalCampaign && (
                <>
                  {approvalAction === 'approve' 
                    ? 'Approve this campaign to allow it to participate in bidding.'
                    : 'Reject this campaign. It will be paused and cannot participate in bidding.'}
                  <br />
                  <strong className="text-slate-800">{approvalCampaign.name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Campaign Info */}
            {approvalCampaign && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Status:</span>
                    <span className="ml-2 text-slate-800">{approvalCampaign.status}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Bid CPM:</span>
                    <span className="ml-2 text-slate-800">${approvalCampaign.bid_price?.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Advertiser:</span>
                    <span className="ml-2 text-slate-800">{approvalCampaign.advertiser_name || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Run Mode:</span>
                    <span className="ml-2 text-slate-800 capitalize">{approvalCampaign.run_mode || 'automatic'}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-slate-700">Notes (optional)</Label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder={approvalAction === 'approve' 
                  ? "Add approval notes..." 
                  : "Reason for rejection..."}
                className="border-slate-200 min-h-[80px]"
                data-testid="approval-notes"
              />
            </div>
            
            {/* Warning for rejection */}
            {approvalAction === 'reject' && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-sm text-red-700">
                <strong>Warning:</strong> Rejecting this campaign will automatically pause it.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setApprovalModalOpen(false)}
              className="border-slate-200"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleApproval}
              disabled={savingApproval}
              className={approvalAction === 'approve' 
                ? "bg-green-600 hover:bg-green-700 text-white"
                : "bg-red-600 hover:bg-red-700 text-white"}
              data-testid="confirm-approval-btn"
            >
              {savingApproval 
                ? "Processing..." 
                : approvalAction === 'approve' ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Run Mode Modal (Super Admin only) */}
      <Dialog open={runModeModalOpen} onOpenChange={setRunModeModalOpen}>
        <DialogContent className="surface-primary border-panel sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Settings2 className="w-5 h-5 text-blue-600" />
              Campaign Run Mode
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {runModeCampaign && (
                <>
                  Set how the campaign should run.
                  <br />
                  <strong className="text-slate-800">{runModeCampaign.name}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label className="text-slate-700">Select Run Mode</Label>
              
              <div className="space-y-2">
                {/* Automatic Mode */}
                <div 
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedRunMode === 'automatic' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedRunMode('automatic')}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                      selectedRunMode === 'automatic' ? 'border-blue-500' : 'border-slate-300'
                    }`}>
                      {selectedRunMode === 'automatic' && (
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-slate-800">Automatic</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        Campaign runs directly when bid requests come in. Standard RTB flow.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Manual Mode */}
                <div 
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedRunMode === 'manual' 
                      ? 'border-slate-500 bg-slate-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedRunMode('manual')}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center ${
                      selectedRunMode === 'manual' ? 'border-slate-500' : 'border-slate-300'
                    }`}>
                      {selectedRunMode === 'manual' && (
                        <div className="w-2 h-2 rounded-full bg-slate-500" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Settings2 className="w-4 h-4 text-slate-600" />
                        <span className="font-medium text-slate-800">Manual</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        More control over campaign execution. Requires manual oversight.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
              <strong>Note:</strong> All campaigns still require approval before they can participate in bidding.
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setRunModeModalOpen(false)}
              className="border-slate-200"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRunModeChange}
              disabled={savingRunMode}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-run-mode-btn"
            >
              {savingRunMode ? "Saving..." : "Save Mode"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
